// The master wallet. Anyone can LOOK without one; only acting needs it.
//
// Two ways in:
//   * Adena (browser extension) — window.adena. Used for the one-time session
//     approval and, if the player declines a session, for every call.
//     UNPROVEN against pearl-1 from this page: Adena 1.21.4 bundles
//     gno-js-client 3.x, whose fee sign-shape pearl-1 rejects today (see
//     sign.js). Whether Adena signs with its own code is not known here.
//   * A testnet key pasted into the page (mnemonic), kept in sessionStorage
//     only. This path is the one proven live (scripts/prove-session.mjs).
import { GnoWallet, MsgCall } from '@gnolang/gno-js-client';
import { CONFIG } from '../config.js';
import { provider } from './reads.js';
import { signAndSend } from './sign.js';

export function hasAdena() { return typeof window !== 'undefined' && !!window.adena; }

export async function connectAdena() {
  const a = window.adena;
  const est = await a.AddEstablish('SPLASH');
  if (est?.status === 'failure' && est?.type !== 'ALREADY_CONNECTED') throw new Error(est.message || 'Adena refused');
  const acc = await a.GetAccount();
  if (acc?.status !== 'success') throw new Error(acc?.message || 'Adena: no account');
  const net = await a.GetNetwork();
  if (net?.status === 'success' && net.data?.chainId && net.data.chainId !== CONFIG.chainId) {
    throw new Error(`Adena is on ${net.data.chainId}; switch it to ${CONFIG.chainId}`);
  }
  return { kind: 'adena', address: acc.data.address };
}

// Silent resume, added 2026-10-07 because moving between the three pages dropped
// the connection every time. Each page is a separate document, the master wallet
// lives in memory, and nothing carried it across -- so the arcade felt like it
// forgot you the moment you clicked "Hourly".
//
// IT MUST NOT PROMPT, which is the whole design of it. connectAdena calls
// AddEstablish, and that pops the extension's approval dialog if the site is not
// already established -- firing that on every page load, unasked, would be
// obnoxious and would train people to click through approval dialogs without
// reading them. GetAccount alone does not prompt: it answers only when the
// origin is ALREADY established, which can only have happened because the player
// connected here deliberately at some point.
//
// Returns null, never throws, for every "not connected" case. A resume that
// fails is not an error the player needs to see; the Connect button is right
// there.
// A DELIBERATE DISCONNECT MUST OUTLIVE A PAGE LOAD. Adena has no "forget this
// site" call a page can make -- establishment is the extension's state, not
// ours -- so without this flag, disconnecting and clicking Hourly would silently
// reconnect you, which is worse than having no disconnect at all.
const OFFKEY = 'splash.disconnected.v1';
export function markDisconnected() { try { sessionStorage.setItem(OFFKEY, '1'); } catch (e) {} }
export function clearDisconnected() { try { sessionStorage.removeItem(OFFKEY); } catch (e) {} }
function isDisconnected() { try { return sessionStorage.getItem(OFFKEY) === '1'; } catch (e) { return false; } }

export async function resumeAdena() {
  if (!hasAdena() || isDisconnected()) return null;
  try {
    const acc = await window.adena.GetAccount();
    if (acc?.status !== 'success' || !acc.data?.address) return null;
    const net = await window.adena.GetNetwork();
    // A wallet left on another chain is NOT resumed. Its address is perfectly
    // valid and its entries here would be zero for ever, which reads as "the
    // realm forgot me" rather than "you are on the wrong network" -- and unlike
    // the explicit connect, there is no error message in a silent resume to say
    // so. Better to stay disconnected and let them press the button.
    if (net?.status === 'success' && net.data?.chainId && net.data.chainId !== CONFIG.chainId) return null;
    return { kind: 'adena', address: acc.data.address };
  } catch (e) {
    return null;
  }
}

// Sends messages through Adena. msgs are {type, value} in Adena's JSON shape.
// ONE SIGNATURE FOR MANY MESSAGES. Adena has always taken an array here; the
// arcade just never used it, so buying a ticket from a fresh wallet cost the
// player THREE separate approval dialogs -- wrap, approve, buy. One transaction
// is fewer prompts AND safer: the messages are atomic, so a failure cannot leave
// money wrapped and approved for a purchase that never happened.
//
// GAS SCALES WITH THE BATCH. CONFIG.fee.gas_wanted is sized for one message, and
// Adena does not estimate -- it sends exactly what it is told, so a three-message
// batch on a one-message budget fails with 'out of gas'. Seeding the beacon this
// afternoon failed for precisely that reason at a 54%-low figure.
export async function adenaSend(msgs, gasWanted = null) {
  const want = gasWanted ?? Number(CONFIG.fee.gas_wanted) * Math.max(1, msgs.length);
  const r = await window.adena.DoContract({ messages: msgs, gasFee: Number(CONFIG.fee.gas_fee.replace(/\D/g, '')) * Math.max(1, msgs.length), gasWanted: want });
  if (r?.status !== 'success') throw new Error(r?.message || 'Adena: transaction failed');
  return r.data;
}

const MKEY = 'splash.testkey.v1';
export async function connectMnemonic(mnemonic) {
  const w = await GnoWallet.fromMnemonic(mnemonic.trim());
  w.connect(await provider());
  sessionStorage.setItem(MKEY, mnemonic.trim());
  return { kind: 'key', address: await w.getAddress(), wallet: w };
}
export async function resumeMnemonic() {
  const m = sessionStorage.getItem(MKEY);
  return m ? connectMnemonic(m) : null;
}
export function forgetMnemonic() { sessionStorage.removeItem(MKEY); }

// Master-signed send for the pasted-key path.
export async function keySend(master, protoMsgs, memo = '') {
  const p = await provider();
  const acc = (await p.getAccount(master.address)).BaseAccount;
  const tx = { messages: protoMsgs, fee: CONFIG.fee, memo, signatures: [] };
  const r = await signAndSend({ provider: p, signer: master.wallet.getSigner(), accountNumber: acc.account_number, sequence: acc.sequence, tx, estimate: true, gasPrice: CONFIG.gasPrice });
  const err = r.deliver_tx?.ResponseBase?.Error;
  if (err) throw new Error((r.deliver_tx?.ResponseBase?.Log || '').split('\n').find((l) => /panic|Msg Traces|- /.test(l)) || 'transaction failed');
  return r;
}

// WITH A SEND ENVELOPE. Only wugnot.Deposit needs this: wrapping is the one call
// in the arcade that hands over NATIVE coin instead of pulling through an
// allowance. Every realm this project deploys refuses an attached send outright
// -- that is what rejectAttachedCoin enforces, added to the contracts this
// morning -- so if you find yourself reaching for these two for anything in
// r/splash, the mistake is upstream.
export function callProtoSend(caller, pkgPath, func, args, send) {
  return { type_url: '/vm.m_call', value: MsgCall.encode({ caller, send, max_deposit: '', pkg_path: pkgPath, func, args: args.map(String) }).finish() };
}
export function callJsonSend(caller, pkgPath, func, args, send) {
  return { type: '/vm.m_call', value: { caller, send, pkg_path: pkgPath, func, args: args.map(String) } };
}

export function callProto(caller, pkgPath, func, args) {
  return { type_url: '/vm.m_call', value: MsgCall.encode({ caller, send: '', max_deposit: '', pkg_path: pkgPath, func, args: args.map(String) }).finish() };
}
export function callJson(caller, pkgPath, func, args) {
  return { type: '/vm.m_call', value: { caller, send: '', pkg_path: pkgPath, func, args: args.map(String) } };
}
